import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { format, parseISO } from 'date-fns';
import enUS from 'date-fns/locale/en-US';
import nb from 'date-fns/locale/nb';
import { FileText, FolderOpen, Image, LockKeyhole } from 'lucide-react';

import { Button } from 'src/components/Buttons/Button';
import { Organization, OrganizationDocument } from 'src/domain/organization';
import { OrganizationDocumentPreviewModal } from '../Admin/components/OrganizationDocumentPreviewModal';

type Props = {
    organization: Organization;
};

const getDocumentTitle = (document: OrganizationDocument) =>
    document.displayName?.trim() || document.originalName;

const getFileTypeLabel = (document: OrganizationDocument) =>
    document.originalName.split('.').pop()?.toUpperCase() ?? '';

const formatFileSize = (size?: number) => {
    if (typeof size !== 'number') return undefined;
    if (size < 1024 * 1024) return `${Math.max(1, Math.round(size / 1024))} KB`;
    return `${(size / (1024 * 1024)).toFixed(1)} MB`;
};

export const AppContentDocumentCards = ({ organization }: Props) => {
    const { t, i18n } = useTranslation();
    const locale = i18n.language?.startsWith('en') ? enUS : nb;
    const [previewDocument, setPreviewDocument] =
        useState<OrganizationDocument | null>(null);
    const documents = organization.documents ?? [];

    const getDocumentDetails = (document: OrganizationDocument) =>
        [
            getFileTypeLabel(document),
            formatFileSize(document.size),
            format(parseISO(document.createdDate), 'd MMM yyyy', { locale }),
        ]
            .filter(Boolean)
            .join(' · ');

    return (
        <div className="h-full overflow-y-auto pb-8 text-woodyBrown">
            <div className="space-y-4">
                <div className="flex items-start gap-3 rounded-2xl border border-woodyBrown/15 bg-white bg-opacity-50 px-4 py-3 text-woodyBrown/70">
                    <LockKeyhole
                        className="mt-0.5 h-5 w-5 flex-shrink-0 text-woodyBrown/50"
                        strokeWidth={1.8}
                        aria-hidden="true"
                    />
                    <div>
                        <p className="text-sm font-semibold text-woodyBrown/75">
                            {t('modules.appContent.readOnlyLabel')}
                        </p>
                        <p className="mt-0.5 text-sm leading-5 text-woodyBrown/60">
                            {t('modules.appContent.readOnlyNotice')}
                        </p>
                    </div>
                </div>

                <section className="rounded-2xl bg-white p-5 shadow-sm sm:p-6">
                    <div className="flex items-start gap-4">
                        <span className="flex h-12 w-12 flex-shrink-0 items-center justify-center rounded-full bg-lightPeach">
                            <FolderOpen
                                className="h-6 w-6"
                                strokeWidth={1.8}
                                aria-hidden="true"
                            />
                        </span>
                        <div className="min-w-0 flex-1">
                            <h2 className="break-words font-logo text-2xl leading-tight">
                                {organization.documentsFolderName?.trim() ||
                                    t('modules.appContent.documentsTitle')}
                            </h2>
                            <p className="mt-1 text-sm text-woodyBrown/60">
                                {t('modules.appContent.documentCount', {
                                    count: documents.length,
                                })}
                            </p>
                        </div>
                    </div>

                    {documents.length ? (
                        <ul className="mt-5 divide-y divide-woodyBrown/10 border-t border-woodyBrown/10">
                            {documents.map(document => {
                                const DocumentIcon =
                                    document.mimeType.startsWith('image/')
                                        ? Image
                                        : FileText;
                                const title = getDocumentTitle(document);

                                return (
                                    <li
                                        key={document.id}
                                        className="flex items-center gap-3 py-4"
                                    >
                                        <span className="flex h-10 w-10 flex-shrink-0 items-center justify-center rounded-full bg-lightPeach">
                                            <DocumentIcon
                                                className="h-5 w-5"
                                                strokeWidth={1.8}
                                                aria-hidden="true"
                                            />
                                        </span>
                                        <div className="min-w-0 flex-1">
                                            <p className="break-words font-medium leading-6">
                                                {title}
                                            </p>
                                            <p className="text-sm leading-5 text-woodyBrown/60">
                                                {getDocumentDetails(document)}
                                            </p>
                                        </div>
                                        <Button
                                            title={t(
                                                'admin.documents.previewButton',
                                            )}
                                            onClick={() =>
                                                setPreviewDocument(document)
                                            }
                                        />
                                    </li>
                                );
                            })}
                        </ul>
                    ) : (
                        <p className="mt-5 rounded-2xl bg-lightPeach px-4 py-3 text-sm text-woodyBrown/70">
                            {t('modules.appContent.noDocuments')}
                        </p>
                    )}
                </section>
            </div>

            {previewDocument && (
                <OrganizationDocumentPreviewModal
                    organizationId={organization.id}
                    item={{ document: previewDocument }}
                    onClose={() => setPreviewDocument(null)}
                />
            )}
        </div>
    );
};
